import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Decimal from 'decimal.js';
import { compact } from 'lodash-es';
import { AccountingInvoiceSyncStatusFailureCodeEnum } from 'anchor-ts-infra/src/dto/accounting-invoice/model';
import { InvoiceDisplayStatus } from 'anchor-scout-common/types';
import { Intent } from '~/infra/access-control';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { invoiceModel, relationshipModel, serviceTemplateModel } from '~/models';
import { InvoiceStatus } from '~/models/invoice';
import { InvoiceOptimisticUpdateType } from '~/providers/InvoiceChangesObservableProvider';
import { Accounting } from '~/types/api.model';
import { getAccountingInvoiceErrorMessage } from '~/utils/getAccountingInvoiceErrorMessage';
export const invoiceSearchParam = 'invoice';
export const relationshipSearchParam = 'relationship';
export const terminatedTooltip = 'The agreement is terminated';
export function getFooter(relationship, invoice) {
    const { paymentMethod, feeControls } = relationship;
    return compact([
        paymentMethod && invoiceModel.isPending(invoice) && {
            id: 'due',
            text: "The payment will be processed automatically on ".concat(invoiceModel.paymentProcessingDate(invoice), " using a ").concat((paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.type) === 'creditCard' ? 'credit card' : 'bank account', " ending in ").concat(paymentMethod.last4, ".")
        },
        !feeControls.coverCreditCardFees && (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.type) === 'creditCard' && {
            id: 'cc',
            text: 'Payments made with a credit card are subject to processing fees.'
        }
    ]);
}
export function getURLWithFullPageInvoice(config) {
    const { invoiceId, relationshipId, pathname, search } = config;
    const url = new URL(pathname || location.pathname, location.origin);
    const searchParams = new URLSearchParams(search || location.search);
    searchParams.forEach((value, key)=>url.searchParams.append(key, value));
    url.searchParams.set(invoiceSearchParam, invoiceId);
    url.searchParams.set(relationshipSearchParam, relationshipId);
    return url.pathname + url.search;
}
function syncRefundCallout(param) {
    let { accounting, syncRefundAction } = param;
    return {
        id: 'sync-refund',
        intent: 'info',
        text: "Refund is ready to sync with ".concat(getAccountingDetails(accounting).fullName, "."),
        status: 'pending',
        actions: [
            syncRefundAction
        ]
    };
}
function checkItemServiceTemplateIsLinked(serviceTemplates, item, accounting) {
    const serviceTemplate = item.serviceTemplateId ? serviceTemplates.find((st)=>st.id === item.serviceTemplateId) : undefined;
    return !serviceTemplate || serviceTemplateModel.integratedId({
        serviceTemplate,
        accounting
    });
}
function isItemLinked(item, serviceTemplates, accounting) {
    return getAccountingDetails(accounting).policy.checkServiceMapping({
        item,
        serviceTemplates,
        accounting
    });
}
const syncAfterFailureCallout = (param)=>{
    let { items, contacts, accounting, integration, featureFlags, relationship, retrySyncAction, serviceTemplates, mapContactsAction, mapServicesAction, retrySyncWithoutTimeActivitiesAction } = param;
    return (data)=>{
        const isClientContactLinked = getAccountingDetails(accounting).policy.checkContactsMapping({
            contacts,
            relationship,
            accounting
        });
        const allServicesLinked = items.every((item)=>{
            return featureFlags.SERVICE_TEMPLATE_BUNDLE ? isItemLinked(item, serviceTemplates, accounting) : checkItemServiceTemplateIsLinked(serviceTemplates, item, accounting);
        });
        const failureCode = data.failure.failureCode();
        const failedToMapServices = !allServicesLinked && [
            AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceServiceNotMapped,
            AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientAndServiceNotMapped
        ].includes(failureCode);
        const failedToMapContacts = !isClientContactLinked && [
            AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientNotMapped,
            AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrCodeInvoiceClientAndServiceNotMapped
        ].includes(failureCode);
        const failedToMapTimeActivities = AccountingInvoiceSyncStatusFailureCodeEnum.CustomErrTimeActivityCannotAppliedToInvoice === failureCode;
        const mappingActions = compact([
            failedToMapContacts && mapContactsAction,
            failedToMapServices && mapServicesAction,
            ...failedToMapTimeActivities ? [
                retrySyncWithoutTimeActivitiesAction,
                retrySyncAction
            ] : []
        ]);
        return {
            id: 'sync-after-failure',
            intent: 'warning',
            text: getAccountingInvoiceErrorMessage({
                invoiceIntegration: integration,
                accounting,
                featureFlags
            }),
            status: 'error',
            actions: mappingActions.length > 0 ? mappingActions : [
                retrySyncAction
            ]
        };
    };
};
export function getAccountingSectionCallout(params) {
    const { accounting, integration, invoiceStatus, numRefunds, isZeroAmount, syncAction, syncPaymentAction } = params;
    if (!integration) return null;
    return integration.accept({
        whenPendingSync: ()=>({
                id: 'ready-to-sync',
                intent: 'info',
                text: "Invoice is ready to sync with ".concat(getAccountingDetails(accounting).fullName, "."),
                status: 'pending',
                actions: [
                    syncAction
                ]
            }),
        whenRefundSyncFailed: syncAfterFailureCallout(_object_spread_props(_object_spread({}, params), {
            integration
        })),
        whenInvoiceSyncFailed: syncAfterFailureCallout(_object_spread_props(_object_spread({}, params), {
            integration
        })),
        whenPaymentSyncFailed: syncAfterFailureCallout(_object_spread_props(_object_spread({}, params), {
            integration
        })),
        whenInvoiceRecorded: ()=>{
            if ([
                InvoiceStatus.PaymentConfirmed,
                InvoiceStatus.Paid
            ].includes(invoiceStatus) && !isZeroAmount) return {
                id: 'payment-sync-failed',
                intent: 'info',
                text: "Payment is ready to sync with ".concat(getAccountingDetails(accounting).fullName, "."),
                status: 'pending',
                actions: [
                    syncPaymentAction
                ]
            };
            return null;
        },
        whenVoidedRecorded: ()=>null,
        whenPaymentRecorded: ()=>{
            if (invoiceStatus === InvoiceStatus.Refunded || invoiceStatus === InvoiceStatus.PartiallyRefunded) return syncRefundCallout(params);
            return null;
        },
        whenRefundRecorded: (refundRecorded)=>{
            if (refundRecorded.numRefunds != null && numRefunds != null && refundRecorded.numRefunds < numRefunds) return syncRefundCallout(params);
            return null;
        }
    });
}
export function makeResumePaymentInvoiceAction(isTerminatedRelationship) {
    return {
        text: 'Resume Payment',
        intent: Intent.InvoiceResumePayment,
        disabled: isTerminatedRelationship,
        tooltipTitle: isTerminatedRelationship ? terminatedTooltip : undefined
    };
}
export const getGenericAccountingTooltip = (accounting, itemName)=>/*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            "This line item was mapped in ",
            getAccountingDetails(accounting).fullName,
            " to ",
            /*#__PURE__*/ _jsx("b", {
                children: itemName
            })
        ]
    });
export const getNotVisibleToClientTooltip = ()=>'This indication is not visible to your client.';
export const getXeroTooltip = (param)=>{
    let { accounting, itemName, testId } = param;
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": testId,
        children: [
            getGenericAccountingTooltip(accounting, itemName),
            ". ",
            getNotVisibleToClientTooltip()
        ]
    });
};
export const getItemQBOTooltip = (qbo, testId)=>/*#__PURE__*/ _jsxs("div", {
        "data-testid": testId,
        children: [
            getGenericAccountingTooltip(Accounting.QBO, qbo.item.name),
            qbo.class && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    ' ',
                    "with the Class ",
                    /*#__PURE__*/ _jsx("b", {
                        children: qbo.class.name
                    })
                ]
            }),
            ". ",
            getNotVisibleToClientTooltip()
        ]
    });
export function onRequestRefundSuccess(param) {
    let { now, reason, invoice, sellerId, onRefresh, setInvoice } = param;
    if (invoiceModel.isPaid(invoice) || invoiceModel.isConfirmed(invoice)) {
        const timestamp = now.getTime();
        setInvoice(invoiceModel.setRefunded(invoice, timestamp, now));
        onRefresh({
            optimisticUpdate: {
                type: InvoiceOptimisticUpdateType.Refunded,
                invoiceId: invoice.id,
                timestamp
            }
        });
    } else {
        setInvoice(invoiceModel.setRefundRequested(invoice, sellerId, reason));
        onRefresh({
            optimisticUpdate: {
                type: InvoiceOptimisticUpdateType.RefundRequested,
                invoiceId: invoice.id,
                userId: sellerId,
                reason
            }
        });
    }
}
export function onRequestPaymentConfirmed(param) {
    let { invoice, isVendor, onRefresh, setInvoice } = param;
    setInvoice(invoiceModel.setPaymentConfirmed(invoice, isVendor));
    onRefresh({
        optimisticUpdate: {
            type: InvoiceOptimisticUpdateType.PaymentConfirmed,
            invoiceId: invoice.id,
            isVendor
        }
    });
}
export function showRequestPaymentMethodModal(invoice, relationship) {
    return invoiceModel.isPaymentMethodUnset(invoice) && !relationshipModel.isTerminated(relationship) && (invoice.displayStatus === InvoiceDisplayStatus.Overdue || invoice.displayStatus === InvoiceDisplayStatus.Issued);
}
export function getBadgeSkin(status) {
    switch(status){
        case InvoiceDisplayStatus.Issued:
        case InvoiceDisplayStatus.Voided:
        case InvoiceDisplayStatus.Refunded:
        case InvoiceDisplayStatus.PartiallyRefunded:
            return 'default';
        case InvoiceDisplayStatus.PaymentInitiated:
        case InvoiceDisplayStatus.Paid:
            return 'success';
        case InvoiceDisplayStatus.Overdue:
        case InvoiceDisplayStatus.RefundFailed:
        case InvoiceDisplayStatus.PaymentDisputed:
            return 'error';
    }
}
export const onRequestPartialRefundSuccess = (param)=>{
    let { now, reason, invoice, sellerId, refundFees, refundAmount, onRefresh, setInvoice } = param;
    if (!invoice.payment) return;
    const refunds = invoice.payment.refunds();
    const isFullyRefunded = refunds && refunds.length > 0 ? new Decimal(refunds[refunds.length - 1].amounts.remainingAmount).div(100).sub(new Decimal(refundAmount)).eq(0) : invoiceModel.rawTotalAmount(invoice).eq(new Decimal(refundAmount));
    if (invoiceModel.isPaid(invoice) || invoiceModel.isConfirmed(invoice) || invoiceModel.isPartiallyRefunded(invoice)) {
        const timestamp = now.getTime();
        setInvoice(isFullyRefunded ? invoiceModel.setRefunded(invoice, timestamp, now) : invoiceModel.setPartiallyRefunded(invoice, {
            timestamp,
            fees: invoice.payment.fees(),
            paymentProcessorType: invoice.payment.paymentProcessorType(),
            reason,
            userId: sellerId,
            refundAmount,
            refundFees,
            rawTotalAmount: invoiceModel.rawTotalAmount(invoice)
        }));
        onRefresh({
            optimisticUpdate: _object_spread_props(_object_spread({}, isFullyRefunded ? {
                type: InvoiceOptimisticUpdateType.Refunded
            } : {
                type: InvoiceOptimisticUpdateType.PartiallyRefunded,
                reason,
                refundAmount,
                refundFees,
                fees: invoice.payment.fees(),
                paymentProcessorType: invoice.payment.paymentProcessorType(),
                userId: sellerId,
                rawTotalAmount: invoiceModel.rawTotalAmount(invoice)
            }), {
                invoiceId: invoice.id,
                timestamp
            })
        });
    } else {
        setInvoice(invoiceModel.setPartialRefundRequested({
            invoice,
            userId: sellerId,
            reason,
            refundAmount,
            refundFees
        }));
        onRefresh({
            optimisticUpdate: {
                type: isFullyRefunded ? InvoiceOptimisticUpdateType.RefundRequested : InvoiceOptimisticUpdateType.PartialRefundRequested,
                invoiceId: invoice.id,
                userId: sellerId,
                refundAmount,
                refundFees,
                reason
            }
        });
    }
};
export const getServiceTemplateIdsV1 = (invoice)=>{
    return compact(invoice.items.map((item)=>item.serviceTemplateId));
};
